import { useEffect, useId, useRef } from "react";

/**
 * One side drawer at a time in the 2026 shell. Every drawer (patient peek,
 * flag peek, citation sources, the Ask Circadia AI panel) calls
 * ``useExclusiveDrawer`` with its open state and close callback: opening one
 * announces itself and every other open drawer closes.
 */
const DRAWER_OPENED = "ui2026:drawer-opened";

export const useExclusiveDrawer = (open: boolean, close: () => void) => {
  const id = useId();
  const closeRef = useRef(close);
  closeRef.current = close;

  // Only an open drawer listens (and announces): a long chat renders one
  // closed citation drawer per message, which must not each hold a global
  // listener. The announce runs after the subscribe, so a drawer ignores its
  // own event via the id check.
  useEffect(() => {
    if (!open) return;
    const onOpened = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== id) closeRef.current();
    };
    window.addEventListener(DRAWER_OPENED, onOpened);
    window.dispatchEvent(new CustomEvent(DRAWER_OPENED, { detail: id }));
    return () => window.removeEventListener(DRAWER_OPENED, onOpened);
  }, [open, id]);
};
